import { AppType } from "client/Api/Apps/common";
import {
	ReactElement,
	useCallback
} from "react";
import { useRenderManager } from "../../useRenderManager";
import { LiquitLiveTileContent } from "./LiquitLiveTileContent";
import { navigateToLiquitApp } from "client_legacy/Apps/appNavigation";
import { useRegisterTileUsage } from "../useRegisterTileUsage";
import { TileRenderState } from "../../RenderState/common";
import { useBaseNavigation } from "../useInvokeNavigation";
import { TileContextProvider } from "../../TileContextProvider";
import { Coordinate } from "client/Misc/commonTypes";

export type LiquitTileContentProps = {
	renderState: TileRenderState<AppType.Liquit>;
	onEdit: (() => void) | undefined;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const LiquitTileContent = (props: LiquitTileContentProps): ReactElement => {
	const {
		renderState,
		onEdit,
		onOpenTileMenu
	} = props;

	const [navigationElement, navigateHandler] = useLiquitTileNavigation(renderState, onEdit);

	const {
		dimension,
		data: {
			isConfiguredCorrectly,
			settings
		}
	} = renderState;

	const tileContent: ReactElement = useRenderManager(
		dimension,
		() => (
			<LiquitLiveTileContent
				isConfiguredCorrectly={isConfiguredCorrectly}
				onNavigation={navigateHandler}
				connectionId={settings.connectionId}
				onOpenTileMenu={onOpenTileMenu}
			/>
		)
	);

	return (
		<>
			<TileContextProvider
				renderState={renderState}
			>
				{tileContent}
			</TileContextProvider>

			{navigationElement}
		</>
	);
};
LiquitTileContent.displayName = "LiquitTileContent";

export function useLiquitTileNavigation(
	renderStateIsh: Pick<TileRenderState<AppType.Liquit>, "data" | "dimension" | "interactionState">,
	onEdit: (() => void) | undefined
): [element: ReactElement | null, () => void | Promise<void>] {
	const registerTileUsage: () => void = useRegisterTileUsage(renderStateIsh);

	const {
		environmentAppId,
		settings: { destinationWindow }
	} = renderStateIsh.data;

	return useBaseNavigation(
		renderStateIsh,
		onEdit,
		useCallback(
			(): void => {
				registerTileUsage();

				navigateToLiquitApp({
					id: environmentAppId,
					destinationWindow
				});
			},
			[
				destinationWindow,
				environmentAppId,
				registerTileUsage
			]
		)
	);
}
